import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import Checkbox from "@mui/material/Checkbox";
import {
  useGetProductsPaginatedQuery,
  useGetProductsQuery,
} from "../../productAPI";
import {
  MenuItem,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Tooltip,
} from "@mui/material";

import { CustomScrollbar } from "../customScrollbar/CustomScrollbar";
import SearchIcon from "@mui/icons-material/Search";
import { ClearIcon } from "@mui/x-date-pickers";

function not(a, b) {
  return a.filter((value) => b.indexOf(value) === -1);
}

function intersection(a, b) {
  return a.filter((value) => b.indexOf(value) !== -1);
}

function union(a, b) {
  return [...a, ...not(b, a)];
}

export default function ProductTransferList({
  onExportRightData,
  associatedProducts,
  currentProduct,
}) {
  const [selectedProductType, setSelectedProductType] = useState("");
  const [checked, setChecked] = useState([]);
  const [left, setLeft] = useState([]);
  const [right, setRight] = useState(
    associatedProducts.map((product) => ({
      title: product.title,
      id: product.id,
    })) || []
  );
  const [searchTerm, setSearchTerm] = useState("");

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const handleToggle = (value) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const numberOfChecked = (items) => intersection(checked, items).length;

  const handleToggleAll = (items) => () => {
    if (numberOfChecked(items) === items.length) {
      setChecked(not(checked, items));
    } else {
      setChecked(union(checked, items));
    }
  };

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked));
    setRight(not(right, rightChecked));
    setChecked(not(checked, rightChecked));
  };

  const handleCheckedRight = () => {
    setRight(right.concat(leftChecked));
    setLeft(not(left, leftChecked));
    setChecked(not(checked, leftChecked));
    const selectedProducts = right.map((value) =>
      right.find((product) => product.title === value)
    );
  };

  //Product Type List
  const { data } = useGetProductsPaginatedQuery({
    activeType: "producttypeenums",
  });

  const productTypes = data?.product?.productType || [];

  // Fetch Products based on selected Product Type
  const { data: productsData } = useGetProductsQuery({
    activeType: "products",
    productType: selectedProductType,
  });

  const handleSelectProductType = (productType) => {
    setSelectedProductType(productType || "");
  };

  useEffect(() => {
    if (productsData && productsData.content) {
      const filteredData = productsData.content.filter(
        (product) => product.productType === selectedProductType
      );
      //console.log("Filtered Data:", filteredData);

      // Map of the products in an array of objects with productCode and title
      const productsWithTitlesAndProductCodes = filteredData.map((product) => ({
        id: product.id,
        title: product.title,
        productCode: product.productCode,
      }));

      productsWithTitlesAndProductCodes.sort((a, b) => {
        if (a.title < b.title) {
          return -1;
        }
        if (a.title > b.title) {
          return 1;
        }
        return 0;
      });

      setLeft(productsWithTitlesAndProductCodes);
    }
  }, [productsData, selectedProductType]);

  useEffect(() => {
    setSelectedProductType("");
    setLeft([]);
    setRight(
      associatedProducts.map((product) => ({
        id: product.id,
        title: product.title,
        productCode: product.productCode,
      })) || []
    );
    setChecked([]);
  }, [currentProduct]);

  //export right data

  const exportRightData = () => {
    onExportRightData(right);
  };

  useEffect(() => {
    exportRightData();
    console.log("Right Side Data:", right);
  }, [right]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const filteredLeft = left.filter(
    (item) =>
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.productCode.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const customList = (title, items, isLeft = false) => {
    const itemList = Array.isArray(items) ? items : [];

    return (
      <Card sx={{ height: "40vh", display: "flex", flexDirection: "column" }}>
        <CardHeader
          sx={{ px: 2, py: 1 }}
          avatar={
            <Checkbox
              onClick={handleToggleAll(itemList)}
              checked={
                numberOfChecked(itemList) === itemList.length &&
                itemList.length !== 0
              }
              indeterminate={
                numberOfChecked(itemList) !== itemList.length &&
                numberOfChecked(itemList) !== 0
              }
              disabled={itemList.length === 0}
              inputProps={{ "aria-label": "all items selected" }}
            />
          }
          title={title}
          subheader={`${numberOfChecked(itemList)}/${itemList.length} selected`}
        />

        {isLeft && (
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              style: { fontSize: "0.8rem" },
              startAdornment: (
                <InputAdornment position="start">
                  <Tooltip
                    title="Search by Title or by Product Code"
                    aria-label="Search by Title or by Product Code"
                  >
                    <SearchIcon sx={{ fontSize: "1rem", p: 0 }} />
                  </Tooltip>
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <Tooltip title="Clear" aria-label="Clear">
                    <IconButton
                      onClick={() => setSearchTerm("")}
                      edge="end"
                      size="small"
                      sx={{ p: 0 }}
                    >
                      <ClearIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ) : null,
            }}
            sx={{ marginX: 3, marginTop: 2 }}
          />
        )}

        <CustomScrollbar>
          <List
            sx={{
              width: "100%",
              height: "100%",
              bgcolor: "background.paper",
              overflow: "auto",
              flexGrow: 1,
            }}
            dense
            component="div"
            role="list"
          >
            {itemList.length === 0 ? (
              <>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ padding: 2, textAlign: "center" }}
                >
                  No Products were found with the specified title or product
                  code.
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ padding: 2, textAlign: "center" }}
                >
                  Please try again.
                </Typography>
              </>
            ) : (
              itemList.map((value) => {
                const labelId = `transfer-list-all-item-${value.id}-label`;
                return (
                  <ListItemButton
                    key={value.id}
                    role="listitem"
                    onClick={handleToggle(value)}
                  >
                    <ListItemIcon>
                      <Checkbox
                        checked={checked.indexOf(value) !== -1}
                        tabIndex={-1}
                        disableRipple
                        inputProps={{ "aria-labelledby": labelId }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      id={labelId}
                      primary={
                        <>
                          <Typography variant="caption">
                            <b>{value.title}</b>
                          </Typography>
                          <br></br>
                          <Typography variant="caption">
                            Product Code: {value.productCode}
                          </Typography>
                        </>
                      }
                    />
                  </ListItemButton>
                );
              })
            )}
          </List>
        </CustomScrollbar>
      </Card>
    );
  };

  return (
    <>
      <Typography
        variant="subtitle2"
        sx={{
          position: "absolute",
          padding: "5px",
          marginLeft: "10px",
          transform: "translateY(-50%)",
          color: "#7d7d7d",
          zIndex: "2",
          backgroundImage:
            "linear-gradient(to bottom, #ffffff7c 50%, rgba(12, 56, 89, 0) 60%)",
        }}
      >
        Products
      </Typography>
      <List
        sx={{
          border: 1,
          borderColor: "lightgrey",
          boxShadow: 1,
          borderRadius: "5px",
          bgcolor: "rgba(12, 55, 89, 0.06)",
          paddingY: "25px",
        }}
      >
        <Grid
          container
          spacing={2}
          sx={{ marginBottom: 2, paddingX: 4, paddingY: 2 }}
        >
          <Grid xs={12}>
            <Typography variant="subtitle2">
              Currently associated Product(s):
            </Typography>

            <Typography variant="body2" component="div">
              {currentProduct?.products?.length === 0 ? (
                <i>No products are currently associated.</i>
              ) : (
                currentProduct?.products?.map((product, index) => (
                  <React.Fragment key={index}>
                    <b>{product.title}</b>
                    {index < currentProduct?.products?.length - 1 && <b>, </b>}
                  </React.Fragment>
                ))
              )}
            </Typography>
          </Grid>

          <Grid xs={12}>
            <Typography
              variant="subtitle2"
              marginTop="1em"
              marginBottom="1.5em"
            >
              Please, select a <b>Product Type</b> to start editing the
              associated Product(s) if needed
            </Typography>
            <TextField
              InputLabelProps={{ shrink: true }}
              color="primary"
              fullWidth
              label="Product Type"
              select
              value={selectedProductType}
              onChange={(event) => handleSelectProductType(event.target.value)}
            >
              {productTypes.map((productType) => (
                <MenuItem key={productType} value={productType}>
                  <Typography variant="subtitle2">{productType}</Typography>
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        {selectedProductType && (
          <Grid container paddingX={2.5}>
            <Typography variant="subtitle2" marginBottom="1.5em">
              Now move the <b>Product(s)</b> to the right to associate it/them,
              or move it/them to the left to disassociate.
            </Typography>

            <Grid
              container
              spacing={2}
              justifyContent="center"
              alignItems="flex-start"
              sx={{ height: "auto" }}
            >
              <Grid item xs={12} lg={5}>
                {customList("Available", filteredLeft, true)}
              </Grid>

              <Grid item direction="column" alignSelf="center">
                <Grid container direction="column" alignItems="center">
                  <Tooltip
                    title="Move selected to the right"
                    aria-label="move selected right"
                  >
                    <IconButton
                      sx={{ my: 0.5 }}
                      size="small"
                      onClick={handleCheckedRight}
                      disabled={leftChecked.length === 0}
                    >
                      <KeyboardArrowRightIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title="Move selected to the left"
                    aria-label="move selected left"
                  >
                    <IconButton
                      sx={{ my: 0.5 }}
                      size="small"
                      onClick={handleCheckedLeft}
                      disabled={rightChecked.length === 0}
                    >
                      <KeyboardArrowLeftIcon />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>

              <Grid item xs={12} lg={5}>
                {right.length === 0 ? (
                  <Typography variant="body1" color="textSecondary">
                    No Products are currently associated
                  </Typography>
                ) : (
                  customList("Associated", right)
                )}
              </Grid>
            </Grid>
          </Grid>
        )}
      </List>
    </>
  );
}
